/*
  assets/gtender-dashboard-free.js — GTENDER-DASHBOARD-FREE-G22-01
  Owner: read-only Free dashboard projection for device-local saved tenders.
  Source contract: reads the canonical GTender Free bookmark storage keys only; never writes, fetches,
  infers Pro access, or becomes an identity / entitlement owner.
*/
(function () {
  'use strict';

  var VERSION = 'gtender-dashboard-free-g22-01';
  var ROOT_SELECTOR = '[data-gtender-dashboard-root][data-native-session-required]';
  var ACCOUNT_STAR_KEY_PREFIX = 'gtender.free.stars.v2:';
  var SUBJECT_RE = /^littleg_[a-f0-9]{40}$/;
  var SHARED_TENDER_ID_RE = /^[A-Za-z0-9._:-]{3,240}$/;
  var stateByRoot = new WeakMap();
  var mounted = [];

  function clean(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function isZh(root) {
    var raw = clean(root && root.getAttribute('data-locale')) || clean(document.documentElement.lang) || 'en';
    return /^zh/i.test(raw);
  }

  function copy(root) {
    var isZhHans = /^zh-CN$/i.test(clean(root && root.getAttribute('data-locale')) || clean(document.documentElement.lang));
    return isZh(root)
      ? {
          empty: isZhHans ? '为第一条招标收藏' : '為第一個招標收藏',
          emptyHelp: isZhHans ? '浏览招标时点选书签图标，稍后可在这里查看。' : '瀏覽招標時按下書籤圖標，稍後可在這裡查看。',
          unavailable: '招標資料暫時不可用',
          storageUnavailable: '暫時無法讀取此裝置的已收藏招標',
          storageUnavailableHelp: '目前不會把資料不可讀誤當成空清單；請返回公開 GTender 檢查此裝置的已收藏清單。',
          open: '查看招標',
          more: '前往 GTender 查看全部已收藏招標'
        }
      : {
          empty: 'Star your first tender',
          emptyHelp: 'Star a tender while browsing to find it here later.',
          unavailable: 'Tender details temporarily unavailable',
          storageUnavailable: 'Saved tenders on this device are temporarily unavailable',
          storageUnavailableHelp: 'Unreadable device data is not being treated as an empty list. Open public GTender to review this device’s saved list.',
          open: 'View tender',
          more: 'Open GTender to view all saved tenders'
        };
  }

  function sessionSubject() {
    var session = window.LittleGWebSession;
    var state = session && typeof session.getState === 'function' ? session.getState() : null;
    var subject = clean(state && state.profile && (state.profile.id || state.profile.subject));
    if (!state || state.status !== 'authenticated' || !SUBJECT_RE.test(subject)) return '';
    return subject;
  }

  function readRecords(subject) {
    if (!subject) return { ok: true, records: [] };
    try {
      var raw = window.localStorage.getItem(ACCOUNT_STAR_KEY_PREFIX + subject);
      var parsed = raw === null ? [] : JSON.parse(raw);
      if (!Array.isArray(parsed)) return { ok: false, records: [] };
      var seen = new Set();
      var records = parsed.filter(function (record) {
        var id = clean(record && record.id);
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      }).map(function (record) {
        return {
          id: clean(record.id).slice(0, 240),
          title: clean(record.title).slice(0, 500),
          department: clean(record.department).slice(0, 240),
          deadline: clean(record.deadline).slice(0, 160)
        };
      });
      return { ok: true, records: records };
    } catch (_error) {
      return { ok: false, records: [] };
    }
  }

  function tenderUrl(root, id) {
    var key = clean(id);
    if (!SHARED_TENDER_ID_RE.test(key)) return '';
    try {
      var url = new URL(root.getAttribute('data-radar-url') || '/pages/gtender', window.location.origin);
      url.searchParams.set('tender', key);
      return url.pathname + url.search + url.hash;
    } catch (_error) {
      return '';
    }
  }

  function emptyState(root, list) {
    var strings = copy(root);
    var empty = document.createElement('div');
    empty.className = 'gtender-dashboard__empty';
    var strong = document.createElement('strong');
    strong.textContent = strings.empty;
    var p = document.createElement('p');
    p.textContent = strings.emptyHelp;
    empty.appendChild(strong);
    empty.appendChild(p);
    list.appendChild(empty);
  }

  function storageUnavailableState(root, list) {
    var strings = copy(root);
    var unavailable = document.createElement('div');
    unavailable.className = 'gtender-dashboard__empty';
    unavailable.setAttribute('role', 'status');
    var strong = document.createElement('strong');
    strong.textContent = strings.storageUnavailable;
    var p = document.createElement('p');
    p.textContent = strings.storageUnavailableHelp;
    unavailable.appendChild(strong);
    unavailable.appendChild(p);
    list.appendChild(unavailable);
  }

  function render(root) {
    var summary = root.querySelector('[data-gtender-dashboard-free-summary]');
    var count = root.querySelector('[data-gtender-dashboard-free-count]');
    var list = root.querySelector('[data-gtender-dashboard-free-list]');
    if (!summary || !count || !list) return;

    var isFree = root.getAttribute('data-account-state') === 'free';
    var subject = sessionSubject();

    count.textContent = '';
    list.replaceChildren();
    summary.hidden = !isFree || !subject;

    if (!isFree || !subject) return;

    var result = readRecords(subject);
    if (!result.ok) {
      count.textContent = '—';
      storageUnavailableState(root, list);
      return;
    }

    var records = result.records;
    count.textContent = String(records.length);

    if (!records.length) {
      emptyState(root, list);
      return;
    }

    records.slice(0, 5).forEach(function (record) {
      var row = document.createElement('article');
      row.className = 'gtender-dashboard__row';

      var title = document.createElement('strong');
      title.textContent = record.title || copy(root).unavailable;
      row.appendChild(title);

      var meta = document.createElement('div');
      meta.className = 'gtender-dashboard__meta';
      if (record.department) {
        var department = document.createElement('span');
        department.textContent = record.department;
        meta.appendChild(department);
      }
      if (record.deadline) {
        var deadline = document.createElement('span');
        deadline.textContent = record.deadline;
        meta.appendChild(deadline);
      }
      if (meta.childNodes.length) row.appendChild(meta);

      var href = tenderUrl(root, record.id);
      if (href) {
        var link = document.createElement('a');
        link.className = 'gtender-dashboard__button';
        link.href = href;
        link.textContent = copy(root).open;
        row.appendChild(link);
      }
      list.appendChild(row);
    });
  }

  function mount(root) {
    if (!root || stateByRoot.has(root)) return;

    var state = {
      controller: typeof AbortController === 'function' ? new AbortController() : null,
      observer: null,
      refresh: null,
      storage: null
    };
    stateByRoot.set(root, state);
    mounted.push(root);
    root.setAttribute('data-free-dashboard-version', VERSION);

    state.refresh = function () {
      if (!root.isConnected) return;
      render(root);
    };
    state.storage = function (event) {
      if (event.key === null || String(event.key || '').indexOf(ACCOUNT_STAR_KEY_PREFIX) === 0) state.refresh();
    };

    if (state.controller) {
      var signal = state.controller.signal;
      document.addEventListener('gtender:bookmarks:changed', state.refresh, { signal: signal });
      document.addEventListener('littleg:web-session-change', state.refresh, { signal: signal });
      window.addEventListener('pageshow', state.refresh, { signal: signal });
      window.addEventListener('storage', state.storage, { signal: signal });
    } else {
      document.addEventListener('gtender:bookmarks:changed', state.refresh);
      document.addEventListener('littleg:web-session-change', state.refresh);
      window.addEventListener('pageshow', state.refresh);
      window.addEventListener('storage', state.storage);
    }

    if (typeof MutationObserver === 'function') {
      state.observer = new MutationObserver(state.refresh);
      state.observer.observe(root, { attributes: true, attributeFilter: ['data-account-state'] });
    }
    render(root);
  }

  function destroy(root) {
    var state = root ? stateByRoot.get(root) : null;
    if (!state) return;
    if (state.controller) state.controller.abort();
    else {
      document.removeEventListener('gtender:bookmarks:changed', state.refresh);
      document.removeEventListener('littleg:web-session-change', state.refresh);
      window.removeEventListener('pageshow', state.refresh);
      window.removeEventListener('storage', state.storage);
    }
    if (state.observer) state.observer.disconnect();
    stateByRoot.delete(root);
    mounted = mounted.filter(function (candidate) { return candidate !== root; });
    root.removeAttribute('data-free-dashboard-version');
  }

  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) mount(target);
    if (target.querySelectorAll) Array.prototype.forEach.call(target.querySelectorAll(ROOT_SELECTOR), mount);
  }

  function destroyAll(scope) {
    var target = scope || document;
    mounted.slice().forEach(function (root) {
      if (target === document || root === target || (target.contains && target.contains(root))) destroy(root);
    });
  }

  document.addEventListener('shopify:section:load', function (event) { boot(event.target || document); });
  document.addEventListener('shopify:section:unload', function (event) { destroyAll(event.target || document); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  } else {
    boot(document);
  }
})();
